// ======================================================================
// File: src/modules/ppe/components/boq/OrderItemBOQCard.js
// ======================================================================

import React, { useEffect, useState } from "react";
import ppeOrderService from "../../services/ppeOrderService";
import BOQLinesTable from "./BOQLinesTable";

// ------------------------------------------------------------------
const BOQ_STATUS_MAP = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

export default function OrderItemBOQCard({ item }) {
  const [boq, setBoq] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // ------------------------------------------------------------
  async function loadBOQ() {
    setLoading(true);
    try {
      const res = await ppeOrderService.getActiveBOQ(item.id);
      setBoq(res || null);

      if (res?.id) {
        const l = await ppeOrderService.listBOQLines(res.id);
        setLines(l || []);
      } else {
        setLines([]);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBOQ();
  }, [item.id]);

  // ------------------------------------------------------------
  const statusCode = boq?.status_code;
  const statusLabel = statusCode ? BOQ_STATUS_MAP[statusCode] : null;

  const isDraft = statusCode === "DRAFT";
  const isSubmitted = statusCode === "SUBMITTED";
  const isRejected = statusCode === "REJECTED";
  const isFrozen = boq?.is_frozen;

  // Auto-expand drafts only
  useEffect(() => {
    if (boq) setExpanded(isDraft);
  }, [boq?.id]);

  // ------------------------------------------------------------
  async function safeAction(fn) {
    try {
      setActionLoading(true);
      await fn();
      await loadBOQ();
    } finally {
      setActionLoading(false);
    }
  }

  // ------------------------------------------------------------
  return (
    <div className="card mb-3">
      {/* ================= HEADER ================= */}
      <div
        className="card-header"
        style={{ cursor: boq ? "pointer" : "default" }}
        onClick={() => boq && setExpanded((e) => !e)}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <strong>
  {expanded ? "▼" : "▶"} Item #{item.item_seq_no} |{" "}
  {item.product_variant_name
    ? `${item.product_variant_code} – ${item.product_variant_name}`
    : `Product Variant #${item.product_variant_id}`}
</strong>

            <div className="text-muted small">
              Quantity: {item.quantity}
            </div>

            {boq && (
              <div className="mt-1">
                <span className="badge bg-secondary me-1">
                  Version {boq.boq_version}
                </span>

                <span className="badge bg-info me-1">
                  {statusLabel}
                </span>

                {isFrozen && (
                  <span className="badge bg-danger">
                    Frozen
                  </span>
                )}
              </div>
            )}
          </div>

          {!boq && (
            <button
              className="btn btn-sm btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                safeAction(() =>
                  ppeOrderService.createBOQ(item.id)
                );
              }}
              disabled={actionLoading}
            >
              + Create BOQ
            </button>
          )}
        </div>
      </div>

      {/* ================= BODY ================= */}
      {expanded && boq && (
        <div className="card-body">
          {/* -------- ACTIONS -------- */}
          <div
            className="d-flex justify-content-end mb-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="btn-group btn-group-sm">
              {isDraft && !isFrozen && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() =>
                    safeAction(() =>
                      ppeOrderService.submitBOQ(boq.id)
                    )
                  }
                  disabled={actionLoading}
                >
                  Submit
                </button>
              )}

              {isSubmitted && (
                <>
                  <button
                    className="btn btn-outline-success"
                    onClick={() =>
                      safeAction(() =>
                        ppeOrderService.approveBOQ(boq.id)
                      )
                    }
                    disabled={actionLoading}
                  >
                    Approve
                  </button>

                  <button
                    className="btn btn-outline-danger"
                    onClick={() => {
                      const reason = prompt("Enter rejection reason");
                      if (reason)
                        safeAction(() =>
                          ppeOrderService.rejectBOQ(boq.id, reason)
                        );
                    }}
                    disabled={actionLoading}
                  >
                    Reject
                  </button>
                </>
              )}

              {isRejected && (
                <button
                  className="btn btn-outline-warning"
                  onClick={() =>
                    safeAction(() =>
                      ppeOrderService.replanBOQ(boq.id)
                    )
                  }
                  disabled={actionLoading}
                >
                  Replan BOQ
                </button>
              )}
            </div>
          </div>

          {/* -------- BOQ LINES -------- */}
          <BOQLinesTable
            lines={lines}
            boqId={boq.id}
            canEdit={isDraft && !isFrozen}
            onReload={loadBOQ}
          />
        </div>
      )}
    </div>
  );
}
